import { CellId, StaticCellId, asciiCompare, notEmpty } from "@hex/common";
import { rgba } from "polished";
import React, { useMemo } from "react";
import { shallowEqual } from "react-redux";
import { createSelector } from "reselect";
import styled from "styled-components";

import { useCellContentsSelector } from "../../hex-version-multiplayer/state-hooks/cellContentsStateHooks";
import { useCellsSelector } from "../../hex-version-multiplayer/state-hooks/cellsStateHooks";
import { useStaticCellSelector } from "../../hex-version-multiplayer/state-hooks/cellStateHooks";
import { CellContentsMP, CellMP } from "../../redux/slices/hexVersionMPSlice";
import { getCellLabel } from "../../util/cellLayoutHelpers";

const Wrapper = styled.div<{ $isDeleted?: boolean }>`
  align-self: flex-start;
  margin: 10px 0 3px;
  padding-left: 6px;

  color: ${({ $isDeleted, theme }) =>
    $isDeleted ? theme.intent.DANGER : theme.fontColor.MUTED};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  line-height: 18px;

  border-left: 2px solid
    ${({ $isDeleted, theme }) =>
      $isDeleted ? rgba(theme.intent.DANGER, 0.3) : theme.borderColor.DEFAULT};
  cursor: default;
`;

interface CommentCellLabelProps {
  readonly staticCellId: StaticCellId;
}

const selectSortedCells = createSelector(
  (cellsState: Record<CellId, CellMP | undefined>) => cellsState,
  (cellsState) =>
    Object.values(cellsState)
      .filter(notEmpty)
      .filter((cell) => cell.deletedDate == null)
      .sort((a, b) => asciiCompare(a.order, b.order)),
);

const makeSelectCellIndex = (): ((
  cellsState: Record<CellId, CellMP | undefined>,
  staticCellId: StaticCellId,
) => number) =>
  createSelector(
    selectSortedCells,
    (_: Record<CellId, CellMP | undefined>, staticCellId: StaticCellId) =>
      staticCellId,
    (sortedCells, staticCellId) =>
      sortedCells.findIndex((c) => c.staticId === staticCellId),
  );

export const CommentCellLabel: React.ComponentType<CommentCellLabelProps> =
  React.memo(function CommentCellLabel({ staticCellId }) {
    const selectCellIndex = useMemo(() => makeSelectCellIndex(), []);

    const cellIndex = useCellsSelector({
      selector: (cellsState) => selectCellIndex(cellsState, staticCellId),
    });

    const { cellId, rawLabel } = useStaticCellSelector({
      staticCellId,
      selector: (cellState: CellMP | undefined) => ({
        cellId: cellState?.id,
        rawLabel: cellState?.label,
      }),
      safe: true,
      equalityFn: shallowEqual,
    });

    // If a cell has been deleted, the rawLabel will be undefined
    // If a cell doesn't have an explicit title, the rawLabel will be null
    const isDeletedCell = rawLabel === undefined;

    const label = useCellContentsSelector({
      cellId,
      selector: (cellContentsState: CellContentsMP | undefined) => {
        return cellContentsState
          ? getCellLabel(
              cellIndex,
              !isDeletedCell ? rawLabel : "",
              cellContentsState,
            )
          : undefined;
      },
      safe: true,
    });

    const labelToShow = !isDeletedCell ? label : "Deleted cell";

    return <Wrapper $isDeleted={isDeletedCell}>{labelToShow}</Wrapper>;
  });
